import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CreatePositionRequest } from '../types/Company';
import styles from './DepartmentModal.module.css';

type PositionOption = {
  id: string;
  label: string;
};

type PositionModalProps = {
  isOpen: boolean;
  isEditing: boolean;
  form: CreatePositionRequest;
  departmentOptions: PositionOption[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onChange: (form: CreatePositionRequest) => void;
  onSave: () => void;
};

export const PositionModal: React.FC<PositionModalProps> = ({
  isOpen,
  isEditing,
  form,
  departmentOptions,
  saving,
  error,
  onClose,
  onChange,
  onSave,
}) => {
  const { t } = useTranslation(['companies', 'admin', 'common']);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose, saving]);

  if (!isOpen) {
    return null;
  }

  const title = isEditing ? t('editPosition', { ns: 'companies' }) : t('createPosition', { ns: 'companies' });
  const saveLabel = saving
    ? t('savingEllipsis', { ns: 'admin' })
    : isEditing
      ? t('updatePosition', { ns: 'companies' })
      : t('createPosition', { ns: 'companies' });

  return createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="position-modal-title"
      onClick={() => {
        if (!saving) {
          onClose();
        }
      }}
    >
      <div className={styles.modalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 id="position-modal-title" className={styles.modalTitle}>{title}</h3>
          <button
            className={styles.modalCloseBtn}
            type="button"
            onClick={onClose}
            aria-label={t('close', { ns: 'common' })}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGrid}>
            <label>
              {t('positionName', { ns: 'companies' })}
              <input
                className={`text-input ${styles.fieldInput}`}
                value={form.name || ''}
                onChange={event => onChange({ ...form, name: event.target.value })}
                autoFocus
              />
            </label>
            <label>
              {t('department', { ns: 'admin' })}
              <select
                className={`text-input ${styles.fieldInput}`}
                value={form.departmentId || ''}
                onChange={event => onChange({ ...form, departmentId: event.target.value })}
              >
                <option value="">{t('allDepartments', { ns: 'companies' })}</option>
                {departmentOptions.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className={styles.modalFooter}>
          {error ? <div className="error">{error}</div> : null}
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {isEditing ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
          </button>
          <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PositionModal;
